/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Box, Typography, Button, Divider } from "@mui/material";
import {
  useMap,
  AdvancedMarker,
  Pin,
  InfoWindow,
} from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Circle } from "@/components/circle";
import { getOffsetPosition } from "@/lib/utils/dateUtils";
import PersonIcon from "@mui/icons-material/Person";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DescriptionIcon from "@mui/icons-material/Description";
import VisibilityIcon from "@mui/icons-material/Visibility";

// Function to generate a unique color for each workflow
function generateWorkflowColor(workflowId, index) {
  if (!workflowId) return "#FBBC04"; // Default yellow for unknown workflows
  
  // Predefined color palette for better visual consistency
  const colorPalette = [
    "#FF6B6B", // Red
    "#4ECDC4", // Teal
    "#45B7D1", // Blue
    "#FFA07A", // Light Salmon
    "#98D8C8", // Mint
    "#F7DC6F", // Yellow
    "#BB8FCE", // Purple
    "#85C1E2", // Sky Blue
    "#F8B739", // Orange
    "#52B788", // Green
    "#E56B6F", // Rose
    "#6C5CE7", // Indigo
    "#74B9FF", // Light Blue
    "#FFB8B8", // Pink
    "#A29BFE", // Lavender
    "#FD79A8", // Hot Pink
    "#FDCB6E", // Mustard
    "#00B894", // Emerald
    "#E17055", // Terra Cotta
    "#636E72", // Gray Blue
  ];
  
  // Use index if provided (for consistent coloring), otherwise hash the workflowId
  if (typeof index === "number") {
    return colorPalette[index % colorPalette.length];
  }
  
  // Generate a hash from the workflowId for consistent colors
  let hash = 0;
  const idStr = String(workflowId);
  for (let i = 0; i < idStr.length; i++) {
    hash = idStr.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return colorPalette[Math.abs(hash) % colorPalette.length];
}

function PoiMarkers({ pois, documents, onMarkerClick, t, workflowColorMap }) {
  const map = useMap();
  const [markers, setMarkers] = useState({});
  const [listeners, setListeners] = useState({});
  const clusterer = useRef(null);
  const [circleCenter, setCircleCenter] = useState(null);
  const [infoWindowPosition, setInfoWindowPosition] = useState(null);
  const [infoWindowDocs, setInfoWindowDocs] = useState([]);
  const [showSummary, setShowSummary] = useState(false);

  const docsByLocation = useMemo(() => {
    const grouped = {};
    documents.forEach((doc) => {
      if (doc.site?.latitude && doc.site?.longitude) {
        const key = `${doc.site.latitude},${doc.site.longitude}`;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(doc);
      }
    });
    return grouped;
  }, [documents]);

  const handleMarkerClick = useCallback(
    (docId, lat, lng, docsAtLocation) => {
      map.panTo({ lat, lng });
      setCircleCenter({ lat, lng });
      setInfoWindowPosition({ lat, lng });
      setInfoWindowDocs(docsAtLocation);
      // Show summary view for single documents, list view for multiple
      setShowSummary(docsAtLocation.length === 1);
    },
    [map]
  );

  const handleInfoWindowClose = () => {
    setInfoWindowPosition(null);
    setInfoWindowDocs([]);
    setShowSummary(false);
  };

  const handleViewDetails = (docId) => {
    handleInfoWindowClose();
    onMarkerClick(docId);
  };

  useEffect(() => {
    if (!map || clusterer.current) return;
    clusterer.current = new MarkerClusterer({ map });
  }, [map]);

  useEffect(() => {
    clusterer.current?.clearMarkers();
    clusterer.current?.addMarkers(
      Object.values(markers).filter((marker) => marker)
    );
  }, [markers]);

  const setMarkerRef = (marker, key) => {
    if (!key) {
      return;
    }
    if (marker && markers[key]) return;
    if (!marker && !markers[key]) return;

    setMarkers((prev) => {
      const newMarkers = { ...prev };
      if (marker) {
        newMarkers[key] = marker;
      } else {
        delete newMarkers[key];
      }
      return newMarkers;
    });

    setListeners((prev) => {
      const newListeners = { ...prev };
      if (marker) {
        const [, id, lat, lng] = key.split("|");
        const locKey = `${lat},${lng}`;
        const docsAtLocation = docsByLocation[locKey] || [];
        const listener = window.google.maps.event.addListener(
          marker,
          "gmp-click",
          () =>
            handleMarkerClick(
              id,
              parseFloat(lat),
              parseFloat(lng),
              docsAtLocation
            )
        );
        newListeners[key] = listener;
      } else {
        if (newListeners[key]) {
          window.google.maps.event.removeListener(newListeners[key]);
        }
        delete newListeners[key];
      }
      return newListeners;
    });
  };

  useEffect(() => {
    return () => {
      Object.values(listeners).forEach((listener) => {
        if (listener) {
          window.google.maps.event.removeListener(listener);
        }
      });
      clusterer.current?.clearMarkers();
    };
  }, [listeners]);

  return (
    <>
      <Circle
        radius={800}
        center={circleCenter}
        strokeColor="#0c4cb3"
        strokeOpacity={1}
        strokeWeight={3}
        fillColor="#3b82f6"
        fillOpacity={0.3}
      />
      {pois.map((poi) => {
        const doc = documents.find((d) => d.id === poi.key);
        if (!doc || !poi.key || !poi.location?.lat || !poi.location?.lng) {
          return null;
        }
        const locKey = `${poi.location.lat},${poi.location.lng}`;
        const docsAtLocation = docsByLocation[locKey] || [doc];
        const index = docsAtLocation.findIndex((d) => d.id === poi.key);
        const offsetPosition = getOffsetPosition(
          poi.location,
          index,
          docsAtLocation.length
        );
        
        // Get workflow color
        const workflowId = doc.workflow?.id;
        const markerColor = workflowId && workflowColorMap 
          ? workflowColorMap[workflowId] || "#FBBC04"
          : "#FBBC04";
        
        return (
          <AdvancedMarker
            key={`doc|${poi.key}|${poi.location.lat}|${poi.location.lng}`}
            position={offsetPosition}
            ref={(marker) =>
              setMarkerRef(
                marker,
                `doc|${poi.key}|${poi.location.lat}|${poi.location.lng}`
              )
            }
          >
            <Pin background={markerColor} glyphColor="#000" borderColor="#000" />
          </AdvancedMarker>
        );
      })}
      {infoWindowPosition && infoWindowDocs.length > 0 && (
        <InfoWindow
          position={infoWindowPosition}
          onCloseClick={handleInfoWindowClose}
        >
          <Box sx={{ minWidth: showSummary ? 280 : 200, maxWidth: 400 }}>
            {showSummary && infoWindowDocs[0] ? (
              // Summary view for single document
              <Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <DescriptionIcon sx={{ fontSize: 20, color: "#1976d2" }} />
                  <Typography variant="subtitle2" fontWeight="600">
                    {t("mapSummary.title") || "Work Permit Summary"}
                  </Typography>
                </Box>

                {/* Permit Requested By */}
                <Box sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 0.25,
                    }}
                  >
                    <PersonIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="600"
                      textTransform="uppercase"
                    >
                      {t("mapSummary.requestedBy") || "Requested by"}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="500" sx={{ pl: 2.5 }}>
                    {infoWindowDocs[0].author
                      ? `${infoWindowDocs[0].author.first_name} ${infoWindowDocs[0].author.last_name}`
                      : t("mapSummary.unknownAuthor") || "Unknown"}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                {/* Work Type */}
                <Box sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 0.25,
                    }}
                  >
                    <AccountTreeIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="600"
                      textTransform="uppercase"
                    >
                      {t("mapSummary.workType") || "Work Type"}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="500" sx={{ pl: 2.5 }}>
                    {infoWindowDocs[0].workflow?.name ||
                      t("mapSummary.unknownWorkType") ||
                      "Unknown"}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                {/* Title */}
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 0.25,
                    }}
                  >
                    <DescriptionIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="600"
                      textTransform="uppercase"
                    >
                      {t("mapSummary.titleLabel") || "Title"}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight="500"
                    sx={{ pl: 2.5, wordBreak: "break-word" }}
                  >
                    {infoWindowDocs[0].title ||
                      t("mapSummary.noTitle") ||
                      "No title"}
                  </Typography>
                </Box>

                {/* View Details Button */}
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => handleViewDetails(infoWindowDocs[0].id)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 1,
                  }}
                >
                  {t("mapSummary.viewDetails") || "View Full Details"}
                </Button>
              </Box>
            ) : (
              // List view for multiple documents
              <Box>
                <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                  {t("infoWindowTitle")}
                </Typography>
                {infoWindowDocs.map((doc) => (
                  <Typography
                    key={doc.id}
                    variant="body2"
                    sx={{
                      cursor: "pointer",
                      color: "blue",
                      textDecoration: "underline",
                      mb: 0.5,
                      "&:hover": {
                        textDecoration: "none",
                      },
                    }}
                    onClick={() => handleViewDetails(doc.id)}
                  >
                    {doc.workflow?.name || t("noWorkflow")} -{" "}
                    {doc.title || doc.description || t("untitled")}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </InfoWindow>
      )}
    </>
  );
}

export default PoiMarkers;
export { generateWorkflowColor };
